// 솟다 스플래시 화면 — 앱 부팅 시 1회 재생되는 브랜드 인트로 오버레이
// 2026-06-12 리브랜딩: 날렵 럭셔리 새싹 × 딥 포레스트+골드 (효신님 튜너 확정)
// 시퀀스: 줄기 자람 → 좌잎·우잎 생장 마스크(흔들림) → 워드마크 "솟다/sotda" → 3-dot + 푸터
// 애니메이션 keyframes 는 styles/splash.css

import { useEffect, useState } from "react";
import { SotdaIcon } from "./SotdaIcon";
import "../styles/splash.css";

interface SplashScreenProps {
  /** 재생이 끝나면 호출 — 앱 본화면으로 전환 */
  onFinish?: () => void;
  /** 재생 시작부터 페이드아웃 시작까지의 시간(ms). 기본 3400 (새 시퀀스 2.75s + dot 1회전) */
  duration?: number;
}

// 페이드아웃 길이 — splash.css 의 .is-leaving transition 과 일치시킬 것
const FADE_MS = 300;

export function SplashScreen({ onFinish, duration = 3400 }: SplashScreenProps) {
  // leaving 이 true 가 되면 페이드아웃 시작
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    // duration 후 페이드아웃 시작 → FADE_MS 뒤 onFinish 로 본화면 전환
    const leaveTimer = setTimeout(() => setLeaving(true), duration);
    const finishTimer = setTimeout(() => onFinish?.(), duration + FADE_MS);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(finishTimer);
    };
  }, [duration, onFinish]);

  return (
    <div
      className={`sotda-splash${leaving ? " is-leaving" : ""}`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--sotda-bg)",
        color: "var(--sotda-cream)",
        fontFamily: "var(--font-base)",
      }}
    >
      {/* 아이콘 + 워드마크 묶음 — 시각 중심을 살짝 위로 (시안 기준) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          transform: "translateY(-6%)",
        }}
      >
        <SotdaIcon size={150} />

        <div
          className="sotda-wordmark"
          style={{
            marginTop: 32,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          {/* 한글 워드마크 — Pretendard 800, 크림 */}
          <div style={{ fontWeight: 800, fontSize: 52, lineHeight: 1, letterSpacing: "-0.02em" }}>
            솟다
          </div>
          {/* 영문 서브 — 골드 */}
          <div
            style={{
              fontWeight: 500,
              fontSize: 18,
              lineHeight: 1,
              letterSpacing: "0.12em",
              color: "var(--sotda-gold)",
            }}
          >
            sotda
          </div>
        </div>
      </div>

      {/* 3-dot 로딩 인디케이터 — 기본 opacity 는 splash.css 가 제어 */}
      <div style={{ position: "absolute", bottom: 96, display: "flex", gap: 12 }}>
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={`sotda-dot sotda-dot-${i}`}
            style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--sotda-gold)" }}
          />
        ))}
      </div>

      {/* 푸터 — ink 50% 불투명도 */}
      <div
        className="sotda-footer"
        style={{
          position: "absolute",
          bottom: 28,
          fontSize: 12,
          letterSpacing: "0.02em",
          color: "color-mix(in srgb, var(--sotda-cream) 45%, transparent)",
        }}
      >
        © 2026 sotda · v0.1.0
      </div>
    </div>
  );
}
